import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import UserGeography from './pages/UserGeography';
import Analytics from './pages/Analytics';
import Affiliate from './pages/Affiliate';
import Economics from './pages/Economics';
import Payments from './pages/Payments';
import Safety from './pages/Safety';
import Reports from './pages/Reports';
import ResetAbuse from './pages/ResetAbuse';
import ThreatIntel from './pages/ThreatIntel';
import Errors from './pages/Errors';
import AppUpdates from './pages/AppUpdates';
import Audience from './pages/Audience';

// ── ADMIN AUTH ─────────────────────────────────────────────────────────────
// Admin status is verified server-side via the `amIAdmin` Cloud Function,
// which reads the user's Firebase Custom Claims (set via the `setAdminClaim`
// function). This replaces the old hardcoded ADMIN_EMAILS list, which was
// trivially bypassable: anyone forking this code or modifying the JS bundle
// could change the array and grant themselves admin access.
//
// The bootstrap email-list fallback was removed 2026-05-04 once both Oraku
// admins had been migrated to claims. The custom claim is now the only path.
const amIAdminFn = httpsCallable(functions, 'amIAdmin');

// ── CSS VARIABLES ─────────────────────────────────────────────────────────
const globalStyles = `
  :root {
    --bg:        #0F1419;
    --bg2:       #1A2332;
    --bg3:       #243044;
    --accent:    #008751;
    --accent-dim:#006B40;
    --accent-glow: rgba(0,135,81,0.15);
    --gold:      #D4AF37;
    --gold-dim:  #A67C00;
    --gold-glow: rgba(212,175,55,0.15);
    --border:    rgba(0,135,81,0.15);
    --border2:   rgba(255,255,255,0.06);
    --text1:     #ffffff;
    --text2:     rgba(255,255,255,0.6);
    --text3:     rgba(255,255,255,0.3);
    --green:     #008751;
    --red:       #ef4444;
    --blue:      #3b82f6;
    --purple:    #a855f7;
    --orange:    #f97316;
    --font-display: 'Syne', sans-serif;
    --font-mono:    'DM Mono', monospace;
    --font-body:    'Outfit', sans-serif;
  }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { background:var(--bg); color:var(--text1); font-family:var(--font-body); }
  .admin-layout { display:flex; min-height:100vh; }
  .admin-main { flex:1; margin-left:260px; min-height:100vh; background:var(--bg); }
  @media(max-width:768px){ .admin-main{ margin-left:0; } }
  button { cursor:pointer; font-family:var(--font-body); }
  input, select { font-family:var(--font-body); }
  ::-webkit-scrollbar { width:5px; height:5px; }
  ::-webkit-scrollbar-track { background:var(--bg); }
  ::-webkit-scrollbar-thumb { background:var(--accent); border-radius:3px; }
  ::-webkit-scrollbar-thumb:hover { background:var(--gold); }
`;

export default function App() {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = globalStyles;
    document.head.appendChild(style);

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      setUser(u);
      // Authoritative admin check: ask the server. The function reads the
      // user's ID token claims (`auth.token.admin === true`) — set by
      // `setAdminClaim`, never by the client. The custom claim is the
      // only path that grants admin powers (the bootstrap email-list
      // fallback was removed 2026-05-04).
      try {
        // Force-refresh the ID token first so any newly-granted claim
        // takes effect immediately instead of after the natural ~1h refresh.
        await u.getIdToken(true);
        const result = await amIAdminFn();
        setIsAdmin(result.data?.isAdmin === true);
      } catch (e) {
        console.error('amIAdmin failed:', e);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
      height:'100vh',background:'#0F1419',flexDirection:'column',gap:16}}>
      <div style={{width:52,height:52,border:'3px solid #008751',
        borderTopColor:'transparent',borderRadius:'50%',
        animation:'spin 0.8s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{color:'#D4AF37',fontFamily:'Syne',fontSize:14,letterSpacing:3,
        fontWeight:700}}>ORAKU ADMIN</span>
    </div>
  );

  if (!user || !isAdmin) return <LoginScreen />;

  return (
    <BrowserRouter>
      <div className="admin-layout">
        <Sidebar user={user} onSignOut={() => signOut(auth)} />
        <main className="admin-main">
          <Routes>
            <Route path="/"           element={<Dashboard />} />
            <Route path="/users"      element={<Users />} />
            <Route path="/geography"  element={<UserGeography />} />
            <Route path="/analytics"  element={<Analytics />} />
            <Route path="/payments"   element={<Payments />} />
            <Route path="/economics"  element={<Economics />} />
            <Route path="/safety"     element={<Safety />} />
            <Route path="/reports"    element={<Reports />} />
            <Route path="/resets"     element={<ResetAbuse />} />
            <Route path="/threats"    element={<ThreatIntel />} />
            <Route path="/errors"     element={<Errors />} />
            <Route path="/audience"   element={<Audience />} />
            <Route path="/app-updates" element={<AppUpdates />} />
            <Route path="/affiliate"  element={<Affiliate />} />
            <Route path="*"           element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

// ── LOGIN SCREEN ──────────────────────────────────────────────────────────
function LoginScreen() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState('');
  const [loading,  setLoading]  = useState(false);
  // 'login' | 'reset' — toggles which form is showing.
  const [mode,     setMode]     = useState('login');

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Invalid credentials or not an admin account');
    }
    setLoading(false);
  };

  // Sends a Firebase-hosted password reset email. We ask Firebase to
  // verify the email is a real registered user before sending — this
  // avoids "did the email get sent? did it not?" confusion. If the
  // email isn't registered we surface a vague error rather than
  // confirming the email exists in our system (small anti-enumeration
  // hedge for the admin panel — keeps doors closed without breaking UX).
  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    if (!email.trim()) { setError('Enter the email first.'); setLoading(false); return; }
    // Server-side, rate-limited reset (requestPasswordReset Cloud Function).
    // Same message whatever happens — never reveal whether an account exists.
    try {
      await httpsCallable(functions, 'requestPasswordReset')({ email: email.trim(), platform: 'admin' });
    } catch (_) { /* uniform response */ }
    setInfo('If that email has an account, a reset link is on its way. Check your inbox (and spam).');
    setLoading(false);
  };

  return (
    <div style={{minHeight:'100vh',background:'#0F1419',display:'flex',
      alignItems:'center',justifyContent:'center'}}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        .login-card{animation:fadeUp 0.5s ease}
        .login-input:focus{border-color:#008751 !important;box-shadow:0 0 0 2px rgba(0,135,81,0.2) !important;}
        .link-button{background:none;border:none;padding:0;color:#D4AF37;cursor:pointer;font-size:13px;font-family:inherit}
        .link-button:hover{text-decoration:underline}
      `}</style>
      <div className="login-card" style={{width:420,padding:48,
        background:'#1A2332',border:'1px solid rgba(0,135,81,0.25)',
        borderRadius:20,boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}>
        {/* Logo */}
        <div style={{textAlign:'center',marginBottom:40}}>
          <div style={{width:72,height:72,margin:'0 auto 16px',
            background:'linear-gradient(135deg, rgba(0,135,81,0.2), rgba(212,175,55,0.15))',
            borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center',
            border:'1px solid rgba(0,135,81,0.35)',fontSize:32}}>🎭</div>
          <div style={{fontFamily:'Syne',fontSize:24,fontWeight:800,
            background:'linear-gradient(135deg, #008751, #D4AF37)',
            WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
            ORAKU ADMIN
          </div>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.4)',marginTop:6}}>
            TaxiNaija Technologies LLC
          </div>
          {mode === 'reset' && (
            <div style={{fontSize:13,color:'rgba(255,255,255,0.55)',marginTop:14,lineHeight:1.5}}>
              Enter your admin email and we'll send you a reset link.
            </div>
          )}
        </div>

        {mode === 'login' ? (
          <form onSubmit={login} style={{display:'flex',flexDirection:'column',gap:16}}>
            <input
              className="login-input"
              type="email" placeholder="Admin email" value={email}
              onChange={e => setEmail(e.target.value)} required
              style={{padding:'14px 16px',background:'#0F1419',
                border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:12,color:'#fff',fontSize:14,outline:'none',
                transition:'all 0.2s'}} />
            <input
              className="login-input"
              type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} required
              style={{padding:'14px 16px',background:'#0F1419',
                border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:12,color:'#fff',fontSize:14,outline:'none',
                transition:'all 0.2s'}} />
            {error && <div style={{color:'#ef4444',fontSize:13,textAlign:'center',
              padding:'8px 12px',background:'rgba(239,68,68,0.1)',borderRadius:8}}>
              {error}
            </div>}
            <button type="submit" disabled={loading}
              style={{padding:'14px',background:'linear-gradient(135deg, #008751, #006B40)',
                color:'#fff',border:'none',borderRadius:12,fontSize:15,fontWeight:700,
                fontFamily:'Syne',opacity:loading?0.7:1,marginTop:8,
                transition:'all 0.2s',letterSpacing:0.5}}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{textAlign:'center',marginTop:8}}>
              <button type="button" className="link-button"
                onClick={() => { setMode('reset'); setError(''); setInfo(''); setPassword(''); }}>
                Forgot your password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={requestReset} style={{display:'flex',flexDirection:'column',gap:16}}>
            <input
              className="login-input"
              type="email" placeholder="Admin email" value={email}
              onChange={e => setEmail(e.target.value)} required autoFocus
              style={{padding:'14px 16px',background:'#0F1419',
                border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:12,color:'#fff',fontSize:14,outline:'none',
                transition:'all 0.2s'}} />
            {error && <div style={{color:'#ef4444',fontSize:13,textAlign:'center',
              padding:'8px 12px',background:'rgba(239,68,68,0.1)',borderRadius:8}}>
              {error}
            </div>}
            {info && <div style={{color:'#10b981',fontSize:13,textAlign:'center',
              padding:'8px 12px',background:'rgba(16,185,129,0.1)',borderRadius:8}}>
              {info}
            </div>}
            <button type="submit" disabled={loading}
              style={{padding:'14px',background:'linear-gradient(135deg, #D4AF37, #B8941F)',
                color:'#0F1419',border:'none',borderRadius:12,fontSize:15,fontWeight:700,
                fontFamily:'Syne',opacity:loading?0.7:1,marginTop:8,
                transition:'all 0.2s',letterSpacing:0.5}}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <div style={{textAlign:'center',marginTop:8}}>
              <button type="button" className="link-button"
                onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
                ← Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
